import { memo } from 'react';
import type { IdeaDetailHeaderProps } from './IdeaDetailHeader.types';
import { getWidgetStores } from '@/entities/widget';
import { MotionBox } from '@/shared/ui/motion';
import { AsyncImage } from '../AsyncImage';
import { useTranslation } from 'react-i18next';

/**
 * IdeaDetailHeader - Header section for the idea detail view
 *
 * Features:
 * - Responsive layout (horizontal in fullscreen, vertical in panel mode)
 * - Uses AsyncImage for consistent image loading
 * - Accessible with proper semantic structure
 * - Memoized to prevent unnecessary re-renders
 */
export const IdeaDetailHeader = memo(function IdeaDetailHeader({
  title,
  description,
  mainImage,
}: IdeaDetailHeaderProps) {
  const { t } = useTranslation('contentTest');

  const { sidePanelLayoutMode } = getWidgetStores().uiStore.useSidePanelLayoutMode();
  const isFullscreen = sidePanelLayoutMode === 'fullscreen';

  const imageAltText = t('ideationWidget.ideaDetailHeader.visualRepresentationOf', { title });

  return (
    <MotionBox
      component="header"
      className={`flex w-full items-center justify-between bg-accent px-15 ${
        isFullscreen ? 'flex-row py-15' : 'flex-col py-12'
      }`}
    >
      {/* Left Content */}
      <div className={`flex flex-1 flex-col gap-6 ${isFullscreen ? 'max-w-1/2' : 'max-w-full'}`}>
        <h1 className="text-display-md text-primary">{title}</h1>
        <p className="text-xl text-foreground">{description}</p>
      </div>

      {/* Right Image */}
      <MotionBox
        layout
        className={`relative shrink-0 overflow-hidden rounded-3xl bg-background-emphasis ${
          isFullscreen ? 'aspect-[580/327] h-81.75' : 'mt-12 aspect-[651/367] h-91.75'
        }`}
      >
        <AsyncImage
          assetId={mainImage?.assetId}
          alt={imageAltText}
          variant="dark"
          placeholderText={t('ideationWidget.ideaDetailHeader.noImageAvailable')}
        />
      </MotionBox>
    </MotionBox>
  );
});
